import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, Upload, FileText, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportInterpreter = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [interpreting, setInterpreting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResult(null);
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const interpretReport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setInterpreting(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/report/interpret`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setResult(response.data);
    } catch (err) {
      console.error('Error interpreting report:', err);
      
      // Extract error message from response
      let errorMessage = 'Failed to interpret report. Please try again.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid file format. Please upload JPEG, PNG, WEBP, or GIF images.';
      } else if (err.response?.status === 408) {
        errorMessage = 'Request timed out. Please try with a smaller image.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setInterpreting(false);
    }
  };

  const getSeverityClass = (severity) => {
    if (severity <= 2) return 'severity-low';
    if (severity <= 3) return 'severity-medium';
    return 'severity-high';
  };

  const getSeverityText = (severity) => {
    if (severity <= 2) return 'Low';
    if (severity <= 3) return 'Medium';
    return 'High';
  };

  return (
    <div className="dashboard-container" data-testid="report-interpreter-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-brand">
          <Activity size={28} />
          MedPlexity
        </div>
        <div className="navbar-menu">
          <button 
            className="nav-link" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </nav>

      <div className="content-section">
        <h2 className="section-title" data-testid="page-title">
          <FileText size={32} /> Interpret Medical Report
        </h2>
        <p style={{ color: '#78909c', marginBottom: '2rem' }}>
          Upload X-rays, MRIs, CT scans, or blood test reports for AI-powered interpretation
        </p>

        {/* Upload Area */}
        <div 
          className="upload-area"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.getElementById('file-input').click()}
          data-testid="upload-area"
        >
          <div className="upload-icon">
            <Upload size={64} />
          </div>
          <h3>{file ? file.name : 'Drop your medical report here or click to upload'}</h3>
          <p style={{ color: '#78909c', marginTop: '1rem' }}>
            Supported formats: JPEG, PNG, PDF
          </p>
          <input
            id="file-input"
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            data-testid="file-input"
          />
        </div>

        {error && (
          <div style={{ color: '#d32f2f', marginTop: '1rem', textAlign: 'center' }} data-testid="error-message">
            <AlertTriangle size={20} /> {error}
          </div>
        )}

        {file && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button 
              className="action-btn btn-primary" 
              onClick={interpretReport}
              disabled={interpreting}
              data-testid="interpret-button"
            >
              {interpreting ? 'Analyzing...' : 'Interpret Report'}
            </button>
          </div>
        )}

        {/* Interpretation Result */}
        {result && (
          <div className="interpretation-result" data-testid="interpretation-result">
            <h3 style={{ fontSize: '1.5rem', color: '#0277bd', marginBottom: '1.5rem' }}>
              Interpretation Results
            </h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <strong style={{ fontSize: '1.1rem' }}>Severity Level:</strong>
              <span 
                className={`severity-badge ${getSeverityClass(result.severity)}`}
                style={{ marginLeft: '1rem' }}
                data-testid="severity-badge"
              >
                {getSeverityText(result.severity)} ({result.severity}/5)
              </span>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <strong style={{ fontSize: '1.1rem' }}>Urgency Score:</strong>
              <span 
                className={`severity-badge ${getSeverityClass(result.urgency_score)}`}
                style={{ marginLeft: '1rem' }}
                data-testid="urgency-badge"
              >
                {result.urgency_score}/5
              </span>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#0277bd', marginBottom: '0.5rem' }}>Summary:</h4>
              <p style={{ color: '#455a64', lineHeight: '1.8' }} data-testid="summary-text">{result.summary}</p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#0277bd', marginBottom: '0.5rem' }}>Key Findings:</h4>
              <p style={{ color: '#455a64', lineHeight: '1.8' }} data-testid="findings-text">{result.findings}</p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#0277bd', marginBottom: '0.5rem' }}>Recommendations:</h4>
              <p style={{ color: '#455a64', lineHeight: '1.8' }} data-testid="recommendations-text">{result.recommendations}</p>
            </div>

            {result.differentials && result.differentials.length > 0 && (
              <div>
                <h4 style={{ color: '#0277bd', marginBottom: '0.5rem' }}>Possible Differential Diagnoses:</h4>
                <ul style={{ color: '#455a64', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  {result.differentials.map((diff, index) => (
                    <li key={index} data-testid={`differential-${index}`}>{diff}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportInterpreter;
